import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { showNotification } from "../../../../../constants";
import {
  IMAGE_SIZE_PRESETS,
  formatBytes,
} from "../../../../../utils/imageOptimization";
import {
  runSalesImageOptimization,
  scanSalesImages,
} from "../../hooks/SalesImageCleanup";

/**
 * Scans quicktales.com section content for oversized images (dry run), then on
 * confirmation optimizes them and swaps references. Originals are kept.
 */
const OptimizeImagesModal = ({ open, onClose, onDone }) => {
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) {
      setScan(null);
      setSummary(null);
      setProgress(null);
      return;
    }
    (async () => {
      try {
        setScanning(true);
        setScan(await scanSalesImages());
      } catch (error) {
        console.log("Error scanning images:", error.message);
        showNotification("Error scanning images");
      } finally {
        setScanning(false);
      }
    })();
  }, [open]);

  const todo = scan?.candidates.filter((c) => c.action === "optimize") || [];
  const totalBytes = todo.reduce((s, c) => s + c.size, 0);

  const handleRun = async () => {
    if (
      !window.confirm(
        `Optimize ${todo.length} image(s) and update references in draft and published content? Originals are kept.`
      )
    ) {
      return;
    }
    try {
      setRunning(true);
      const res = await runSalesImageOptimization(scan, setProgress);
      const ok = res.results.filter((r) => r.status === "optimized");
      setSummary({
        optimized: ok.length,
        errors: res.results.filter((r) => r.status === "error").length,
        unchanged: res.results.filter((r) => r.status === "unchanged").length,
        saved: ok.reduce((s, r) => s + (r.size - r.newSize), 0),
        updatedDocs: res.updatedDocs,
      });
      showNotification(`Optimized ${ok.length} image(s)`);
      onDone?.();
    } catch (error) {
      console.log("Error optimizing images:", error.message);
      showNotification("Error optimizing images");
    } finally {
      setRunning(false);
    }
  };

  const renderScanTable = () => (
    <>
      <Typography sx={{ ...textStyles, mb: 1 }}>
        {todo.length} of {scan.candidates.length} referenced image(s) will be
        optimized ({formatBytes(totalBytes)} total). Nothing is changed until
        you click Optimize.
      </Typography>
      {running && progress ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress.total ? (progress.done / progress.total) * 100 : 100}
          />
          <Typography sx={smallStyles}>
            {progress.done}/{progress.total} {progress.current}
          </Typography>
        </Box>
      ) : null}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>File</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Max size</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scan.candidates.map((c) => (
            <TableRow key={c.path}>
              <TableCell sx={{ ...smallStyles, wordBreak: "break-all" }}>
                {c.path}
              </TableCell>
              <TableCell sx={smallStyles}>{formatBytes(c.size)}</TableCell>
              <TableCell sx={smallStyles}>
                {IMAGE_SIZE_PRESETS[c.preset]?.label}
              </TableCell>
              <TableCell sx={smallStyles}>
                {c.action === "optimize" ? "Optimize" : `Skip — ${c.reason}`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={running ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={titleStyles}>Optimize Existing Images</DialogTitle>
      <DialogContent>
        {scanning ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography sx={textStyles}>
              Scanning quicktales.com content…
            </Typography>
          </Box>
        ) : null}

        {summary ? (
          <Typography sx={textStyles}>
            Optimized {summary.optimized} image(s), saving{" "}
            {formatBytes(summary.saved)}. Updated {summary.updatedDocs} section
            record(s). Unchanged: {summary.unchanged}. Errors: {summary.errors}.
            <br />
            Click <b>Save</b> / <b>Publish Page</b> to redeploy staging /
            production. Originals remain in Storage.
          </Typography>
        ) : null}

        {scan && !summary ? renderScanTable() : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={running} style={cancelStyles}>
          {summary ? "Close" : "Cancel"}
        </Button>
        {!summary ? (
          <LoadingButton
            variant="contained"
            loading={running}
            disabled={scanning || todo.length === 0}
            onClick={handleRun}
            style={primaryStyles}
          >
            Optimize {todo.length || ""}
          </LoadingButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default OptimizeImagesModal;

const titleStyles = { fontFamily: "Avenir Book, sans-serif", fontWeight: 700 };

const textStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
};

const smallStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#505050",
};

const cancelStyles = {
  textTransform: "none",
  color: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const primaryStyles = {
  textTransform: "none",
  background: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
